import { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { statusToneClass, type StatusVariant } from './runFormatting';

export type { StatusVariant };

const DOT_CLASSES: Record<StatusVariant, string> = {
  success: 'bg-emerald-400 ring-emerald-400/20',
  warning: 'bg-amber-400 ring-amber-400/20',
  error: 'bg-rose-400 ring-rose-400/20',
  info: 'bg-sky-400 ring-sky-400/25',
  neutral: 'bg-neutral-500 ring-neutral-500/15',
};

const PULSE_CLASSES: Record<StatusVariant, string> = {
  success: 'bg-emerald-400',
  warning: 'bg-amber-400',
  error: 'bg-rose-400',
  info: 'bg-sky-400',
  neutral: 'bg-neutral-500',
};

function StatusDot({
  variant,
  pulse,
  className,
}: {
  variant: StatusVariant;
  pulse?: boolean;
  className?: string;
}) {
  return (
    <span
      className={twMerge(
        'relative inline-flex h-1.5 w-1.5 shrink-0 rounded-full ring-2',
        DOT_CLASSES[variant],
        className,
      )}
      aria-hidden
    >
      {pulse && (
        <span
          className={twMerge(
            'absolute inset-0 inline-flex h-full w-full animate-ping rounded-full opacity-60',
            PULSE_CLASSES[variant],
          )}
        />
      )}
    </span>
  );
}

export function StatusPill({
  variant,
  children,
  className,
}: {
  variant: StatusVariant;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={twMerge(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium tracking-tight',
        statusToneClass(variant),
        className,
      )}
    >
      <StatusDot variant={variant} pulse={variant === 'info'} />
      {children}
    </span>
  );
}

export function StatusInline({
  variant,
  children,
  className,
}: {
  variant: StatusVariant;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={twMerge(
        'inline-flex items-center gap-2 text-[13px] text-neutral-300',
        className,
      )}
    >
      <StatusDot variant={variant} pulse={variant === 'info'} />
      {children}
    </span>
  );
}

export function SectionCard({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={twMerge(
        'overflow-hidden rounded-2xl border border-white/[0.06] bg-neutral-900/40 shadow-[0_1px_0_0_rgba(255,255,255,0.03)_inset]',
        className,
      )}
    >
      {children}
    </section>
  );
}

export function SectionHeader({
  title,
  description,
  actions,
  eyebrow,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={twMerge(
        'flex flex-col gap-4 border-b border-white/[0.06] px-6 py-5 md:flex-row md:items-start md:justify-between',
        className,
      )}
    >
      <div className="min-w-0 space-y-1">
        {eyebrow && (
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-neutral-500">
            {eyebrow}
          </div>
        )}
        <h2 className="text-[15px] font-semibold text-white tracking-tight">
          {title}
        </h2>
        {description && (
          <p className="text-[13px] leading-relaxed text-neutral-400">
            {description}
          </p>
        )}
      </div>
      {actions && (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </header>
  );
}

export function FieldLabel({
  children,
  hint,
}: {
  children: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[12px] font-medium text-neutral-300">
        {children}
      </span>
      {hint && <span className="text-[11px] text-neutral-500">{hint}</span>}
    </div>
  );
}

export const fieldInputClass =
  'w-full rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-[13px] text-white outline-none transition-colors placeholder:text-neutral-600 focus:border-teal-400/40 focus:bg-white/[0.04]';
